import { UserInternal, UserSubscription, UserSubscriptionPaymentStatus } from '~/server/src/dtos';

/**
 * Determines if a user has active premium access (either paid or promotional)
 * @param account - The user account to check
 * @returns true if the user has active premium access, false otherwise
 */
export const hasActivePremiumAccess = (account?: UserInternal): boolean => {
  if (!account) return false;

  // Check if subscription is valid (not expired)
  const now = new Date().getTime();
  const subscriptionValidTime = new Date(account.subscriptionValidUntil).getTime();
  const isSubscriptionValid = subscriptionValidTime >= now;

  // Check if user has premium subscription
  // Note: Payment status is not checked here as it would incorrectly block access
  // for users with active promos who have payment errors or pending payments
  // Also, canceled subscriptions that are still valid continue to provide premium features
  const hasPremiumSubscription = account.subscription === UserSubscription.Premium;

  return hasPremiumSubscription && isSubscriptionValid;
};

/**
 * Determines if a user has premium subscription (regardless of validity)
 * @param account - The user account to check
 * @returns true if the user has premium subscription, false otherwise
 */
export const hasPremiumSubscription = (account?: UserInternal): boolean => {
  if (!account) return false;
  return account.subscription === UserSubscription.Premium;
};

/**
 * Determines if a user's subscription is currently valid (not expired)
 * @param account - The user account to check
 * @returns true if the subscription is valid, false otherwise
 */
export const isSubscriptionValid = (account?: UserInternal): boolean => {
  if (!account) return false;

  const now = new Date().getTime();
  const subscriptionValidTime = new Date(account.subscriptionValidUntil).getTime();
  return subscriptionValidTime >= now;
};

/**
 * Determines if a user has an active promotional subscription
 * @param account - The user account to check
 * @returns true if the user has an active promotional subscription, false otherwise
 */
export const hasPromotionalSubscription = (account?: UserInternal): boolean => {
  if (!account) return false;
  return account.subscriptionIsPromoActive && isSubscriptionValid(account);
};

/**
 * Determines if a user can cancel their subscription
 * @param account - The user account to check
 * @returns true if the subscription can be canceled, false otherwise
 */
export const canCancelSubscription = (account?: UserInternal): boolean => {
  if (!account) return false;

  // Only paid subscriptions can be canceled
  return account.subscriptionPaymentStatus === UserSubscriptionPaymentStatus.Paid;
};

/**
 * Gets the effective subscription type for access control purposes
 * @param account - The user account to check
 * @returns The effective subscription type (Basic or Premium)
 */
export const getEffectiveSubscription = (account?: UserInternal): UserSubscription => {
  if (!account) return UserSubscription.Basic;

  // If subscription is not valid, treat as Basic
  if (!isSubscriptionValid(account)) {
    return UserSubscription.Basic;
  }

  return account.subscription;
};

/**
 * Determines if a user has a pending payment
 * @param account - The user account to check
 * @returns true if the user has a pending payment, false otherwise
 */
export const hasPendingPayment = (account?: UserInternal): boolean => {
  if (!account) return false;
  return account.subscriptionPaymentStatus === UserSubscriptionPaymentStatus.Pending;
};

/**
 * Determines if a user has a payment error
 * @param account - The user account to check
 * @returns true if the user has a payment error, false otherwise
 */
export const hasPaymentError = (account?: UserInternal): boolean => {
  if (!account) return false;
  return account.subscriptionPaymentStatus === UserSubscriptionPaymentStatus.Error;
};

/**
 * Determines if a user has a canceled subscription
 * @param account - The user account to check
 * @returns true if the user has a canceled subscription, false otherwise
 */
export const hasCanceledSubscription = (account?: UserInternal): boolean => {
  if (!account) return false;
  return account.subscriptionPaymentStatus === UserSubscriptionPaymentStatus.Canceled;
};

/**
 * Gets the remaining promo time in days
 * @param account - The user account to check
 * @returns number of days remaining in promo period, 0 if no promo or expired
 */
export const getRemainingPromoDays = (account?: UserInternal): number => {
  if (!account || !account.subscriptionIsPromoActive) return 0;

  const now = new Date().getTime();
  const promoValidTime = new Date(account.subscriptionPromoValidUntil).getTime();
  const remainingMs = promoValidTime - now;

  if (remainingMs <= 0) return 0;

  return Math.ceil(remainingMs / (1000 * 60 * 60 * 24));
};

/**
 * Determines if a user can extend their promo to a paid subscription
 * @param account - The user account to check
 * @returns true if the user can extend their promo, false otherwise
 */
export const canExtendPromo = (account?: UserInternal): boolean => {
  if (!account) return false;

  // User can extend promo if they have an active promo subscription
  // and their payment status is not in error state
  return (
    hasPromotionalSubscription(account) &&
    account.subscriptionPaymentStatus !== UserSubscriptionPaymentStatus.Error
  );
};
